<template>
  <div>
  <v-dialog
    v-model="internalVisible"
    fullscreen
    hide-overlay
    transition="dialog-bottom-transition"
    persistent
  >
    <v-card class="issuance-dialog-card d-flex flex-column" style="height:100vh;">

      <!-- ── TOOLBAR ──────────────────────────────────────────── -->
      <div class="issuance-app-bar flex-shrink-0 d-flex align-center px-3">
        <v-btn icon dark @click="requestClose">
          <v-icon>fas fa-times</v-icon>
        </v-btn>

        <v-avatar color="accent" size="36" class="ml-2 mr-3">
          <v-icon small color="white">fas fa-file-invoice-dollar</v-icon>
        </v-avatar>

        <div>
          <div class="subtitle-1 font-weight-black white--text lh-1">
            {{ referenceDoc ? 'Emitir Nota de Crédito' : 'Emitir Documento Tributario' }}
          </div>
          <div v-if="referenceDoc" class="caption warning--text lh-1 mt-1">
            Referencia: {{ referenceDoc.doc_type_display }} Folio #{{ referenceDoc.folio }}
          </div>
          <div v-else-if="draft.otId" class="caption accent--text lh-1 mt-1">
            OT #{{ draft.otId }} — {{ draft.otTitle }}
          </div>
          <div v-else-if="isGeneralizedMode" class="caption grey--text text--lighten-2 lh-1 mt-1">
            Modo Generalizado — sin OT vinculada
          </div>
        </div>

        <v-spacer />

        <!-- Indicador de borrador guardado -->
        <v-chip
          v-if="draft.savedAt"
          x-small
          outlined
          color="accent"
          class="mr-4 font-weight-bold"
        >
          <v-icon x-small left>fas fa-cloud</v-icon>
          Borrador guardado
        </v-chip>

        <!-- Botón avanzar / emitir -->
        <v-btn
          v-if="currentStep === 1"
          color="accent"
          class="font-weight-black px-6"
          :disabled="!canAdvance || !linesValid"
          @click="goToPreview"
        >
          <v-icon left small>fas fa-eye</v-icon>
          Previsualizar
        </v-btn>

        <v-btn
          v-if="currentStep === 2"
          color="success"
          class="font-weight-black px-6 mr-2"
          :loading="submitting"
          :disabled="submitting || loadingOT || loadingCatalogs || syncIssues.length > 0"
          @click="submitDTE"
        >
          <v-icon left small>fas fa-paper-plane</v-icon>
          Emitir DTE
        </v-btn>

        <v-btn
          v-if="currentStep === 2"
          outlined
          color="white"
          class="font-weight-bold mr-2"
          @click="goBack"
        >
          <v-icon left small>fas fa-chevron-left</v-icon>
          Volver
        </v-btn>
      </div>

      <!-- ── STEPPER INDICATOR ──────────────────────────────────── -->
      <div class="stepper-indicator flex-shrink-0 px-6 py-3 d-flex align-center">
        <div
          v-for="(step, i) in steps"
          :key="i"
          class="step-item d-flex align-center"
          :class="i < steps.length - 1 ? 'mr-6' : ''"
        >
          <div
            class="step-circle mr-2"
            :class="{
              'step-active': currentStep === i + 1,
              'step-done': currentStep > i + 1
            }"
          >
            <v-icon v-if="currentStep > i + 1" x-small color="white">fas fa-check</v-icon>
            <span v-else class="caption font-weight-black">{{ i + 1 }}</span>
          </div>
          <span
            class="caption font-weight-black"
            :class="currentStep === i + 1 ? 'accent--text' : 'grey--text'"
          >{{ step }}</span>
          <v-icon v-if="i < steps.length - 1" x-small color="grey lighten-2" class="ml-6">fas fa-chevron-right</v-icon>
        </div>

        <v-spacer />

        <!-- Historial de documentos emitidos (acordeón) -->
        <div v-if="draft.otId">
          <v-btn
            text small
            color="grey darken-1"
            @click="showHistory = !showHistory"
            class="font-weight-bold"
          >
            <v-icon left x-small>fas fa-history</v-icon>
            Historial ({{ issuedDocuments.length }})
            <v-icon x-small right>{{ showHistory ? 'fas fa-chevron-up' : 'fas fa-chevron-down' }}</v-icon>
          </v-btn>
        </div>
      </div>

      <!-- Historial desplegable -->
      <v-expand-transition>
        <div v-if="showHistory && issuedDocuments.length > 0" class="history-panel px-6 pb-3">
          <v-chip
            v-for="doc in issuedDocuments"
            :key="doc.id"
            small
            outlined
            color="primary"
            class="mr-2 mb-1 font-weight-bold"
          >
            <v-icon x-small left>fas fa-file-alt</v-icon>
            {{ doc.tipo === 30 ? 'Factura' : 'N/C' }} #{{ doc.document_id || doc.id }}
            <span class="ml-1 grey--text">{{ doc.date_emited }}</span>
          </v-chip>
        </div>
      </v-expand-transition>

      <v-divider class="flex-shrink-0" />

      <!-- ── CONTENIDO PRINCIPAL ────────────────────────────────── -->
      <div class="issuance-body flex-grow-1 overflow-y-auto">

        <!-- PASO 1: CONFIGURACIÓN -->
        <div v-show="currentStep === 1" class="pa-6">
          <ValidationObserver ref="stepObserver">
            <!-- Story 4.3: Banner de referencia para Notas de Crédito -->
          <v-alert
            v-if="referenceDoc"
            type="warning"
            text
            dense
            border="left"
            class="mb-4"
          >
            <div class="font-weight-bold mb-1">
              <v-icon small color="warning" class="mr-1">fas fa-link</v-icon>
              Nota de Crédito referenciando {{ referenceDoc.doc_type_display }} Folio #{{ referenceDoc.folio }}
            </div>
            <div class="caption">
              Fecha del documento original: {{ referenceDoc.date_emited }} — Cliente: {{ referenceDoc.customer_name || '—' }}
            </div>
          </v-alert>

          <v-row>

              <!-- Columna izquierda: datos del receptor + selectores Relbase -->
              <v-col cols="12" md="3">

                <!-- Receptor -->
                <v-card outlined class="modern-card pa-5 mb-5">
                  <div class="card-section-title mb-4">
                    <v-icon small color="accent" class="mr-2">fas fa-user-tie</v-icon>
                    Datos del Receptor
                  </div>

                  <div v-if="loadingOT" class="text-center py-6">
                    <v-progress-circular indeterminate color="accent" size="32" />
                  </div>

                  <template v-else>
                  <!-- Story 4.5: Selector de cliente en Modo Generalizado -->
                  <template v-if="isGeneralizedMode">
                    <div class="field-label">Buscar Cliente</div>
                    <CustomerComplete2
                      v-model="generalizedCustomer"
                      class="mb-3"
                      @input="onGeneralizedCustomerSelected"
                    />
                    <v-divider class="mb-3" />
                  </template>
                    <div class="field-label">RUT Receptor</div>
                    <ValidationProvider name="RUT" rules="required" v-slot="{ errors }">
                      <v-text-field
                        v-model="draft.rutRecep"
                        outlined dense
                        class="modern-input mb-3"
                        :class="{
                          'rut-valid': rutValid === true,
                          'rut-invalid': rutValid === false,
                        }"
                        :disabled="rutChecking"
                        :error-messages="errors"
                        @input="persistHeaders"
                        @blur="onRutBlur"
                      >
                        <template v-slot:append>
                          <v-progress-circular
                            v-if="rutChecking"
                            indeterminate size="14" width="2"
                            color="grey lighten-1"
                            class="mt-1"
                          />
                          <v-tooltip v-else-if="rutValid === true" bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <v-icon x-small color="success" v-bind="attrs" v-on="on">fas fa-check-circle</v-icon>
                            </template>
                            <span>Cliente encontrado en Relbase</span>
                          </v-tooltip>
                          <v-tooltip v-else-if="rutValid === false" bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <v-icon x-small color="error" v-bind="attrs" v-on="on">fas fa-times-circle</v-icon>
                            </template>
                            <span>RUT no encontrado en Relbase</span>
                          </v-tooltip>
                        </template>
                      </v-text-field>
                    </ValidationProvider>

                    <div class="field-label">Razón Social</div>
                    <ValidationProvider name="Razón Social" rules="required" v-slot="{ errors }">
                      <v-text-field
                        v-model="draft.rznSocRecep"
                        outlined dense
                        class="modern-input mb-3"
                        :error-messages="errors"
                        @input="persistHeaders"
                      />
                    </ValidationProvider>

                    <div class="field-label">Giro Comercial</div>
                    <v-text-field
                      v-model="draft.giroRecep"
                      outlined dense
                      class="modern-input mb-3"
                      hide-details
                      @input="persistHeaders"
                    />

                    <v-row dense>
                      <v-col cols="8">
                        <div class="field-label">Dirección</div>
                        <v-text-field
                          v-model="draft.dirRecep"
                          outlined dense
                          class="modern-input"
                          hide-details
                          @input="persistHeaders"
                        />
                      </v-col>
                      <v-col cols="4">
                        <div class="field-label">Comuna</div>
                        <v-text-field
                          v-model="draft.cmnaRecep"
                          outlined dense
                          class="modern-input"
                          hide-details
                          @input="persistHeaders"
                        />
                      </v-col>
                    </v-row>

                    <div class="field-label mt-3">Referencia / Glosa</div>
                    <v-text-field
                      v-model="draft.glosa"
                      outlined dense
                      class="modern-input"
                      hide-details
                      placeholder="Descripción del documento..."
                      @input="persistHeaders"
                    />
                  </template>
                </v-card>

                <!-- AC2: Banner de advertencia de sincronización -->
                <v-alert
                  v-if="!loadingOT && !loadingCatalogs && syncIssues.length > 0"
                  type="warning"
                  text
                  dense
                  class="mb-4 caption"
                  border="left"
                >
                  <div class="font-weight-bold mb-1">Sincronización incompleta — no es posible emitir:</div>
                  <ul class="mb-0 pl-3">
                    <li v-for="issue in syncIssues" :key="issue">{{ issue }}</li>
                  </ul>
                </v-alert>

                <!-- Selectores Relbase -->
                <v-card outlined class="modern-card pa-5">
                  <div class="card-section-title mb-4">
                    <v-icon small color="accent" class="mr-2">fas fa-sliders-h</v-icon>
                    Parámetros Relbase
                  </div>

                  <div class="field-label">Vendedor Relbase</div>
                  <v-autocomplete
                    v-model="draft.vendedor"
                    :items="relbaseSellers"
                    item-text="name"
                    item-value="relbase_id"
                    outlined dense
                    class="modern-input mb-3"
                    placeholder="Seleccionar vendedor..."
                    clearable
                    hide-details
                    :loading="loadingCatalogs"
                    @change="persistSelectors"
                  />

                  <div class="field-label">Forma de Pago</div>
                  <v-autocomplete
                    v-model="draft.formaPago"
                    :items="relbasePaymentMethods"
                    item-text="name"
                    item-value="relbase_id"
                    outlined dense
                    class="modern-input mb-3"
                    placeholder="Seleccionar forma de pago..."
                    clearable
                    hide-details
                    :loading="loadingCatalogs"
                    @change="persistSelectors"
                  />

                  <div class="field-label">Bodega</div>
                  <v-autocomplete
                    v-model="draft.bodega"
                    :items="relbaseWarehouses"
                    item-text="name"
                    item-value="relbase_id"
                    outlined dense
                    class="modern-input mb-3"
                    placeholder="Seleccionar bodega..."
                    clearable
                    hide-details
                    :loading="loadingCatalogs"
                    @change="persistSelectors"
                  />

                  <div class="field-label">Canal de Venta</div>
                  <v-autocomplete
                    v-model="draft.canalVenta"
                    :items="relbaseSalesChannels"
                    item-text="name"
                    item-value="relbase_id"
                    outlined dense
                    class="modern-input mb-3"
                    placeholder="Seleccionar canal de venta..."
                    clearable
                    hide-details
                    :loading="loadingCatalogs"
                    @change="persistSelectors"
                  />

                  <div class="field-label">Términos de Pago</div>
                  <v-text-field
                    v-model="draft.terminosPago"
                    outlined dense
                    class="modern-input mb-3"
                    placeholder="Días de crédito o términos..."
                    hide-details
                    @input="persistSelectors"
                  />

                  <!-- Story 4.5: Fecha de Vencimiento (end_date) -->
                  <div class="field-label">Fecha de Vencimiento</div>
                  <v-menu
                    v-model="endDateMenu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        :value="endDateFormatted"
                        outlined dense
                        class="modern-input"
                        placeholder="DD/MM/AAAA"
                        hide-details
                        readonly
                        v-bind="attrs"
                        v-on="on"
                      >
                        <template v-slot:append>
                          <v-icon x-small color="accent">fas fa-calendar-alt</v-icon>
                        </template>
                      </v-text-field>
                    </template>
                    <v-date-picker
                      v-model="endDate"
                      no-title
                      scrollable
                      color="accent"
                      @input="endDateMenu = false"
                    />
                  </v-menu>

                  <!-- Story 4.3: Campos de referencia NC -->
                  <template v-if="referenceDoc">
                    <v-divider class="my-4" />
                    <div class="card-section-title mb-3">
                      <v-icon small color="warning" class="mr-2">fas fa-file-invoice</v-icon>
                      Referencia N/C
                    </div>
                    <div class="field-label">Código de Referencia</div>
                    <v-select
                      v-model="ncCode"
                      :items="ncCodes"
                      item-text="text"
                      item-value="value"
                      outlined dense
                      class="modern-input mb-3"
                      hide-details
                    />
                    <div class="field-label">Razón / Glosa de la N/C</div>
                    <v-text-field
                      v-model="ncReason"
                      outlined dense
                      class="modern-input"
                      placeholder="Ej: Anulación de documento..."
                      hide-details
                    />
                  </template>
                </v-card>

              </v-col>

              <!-- Columna derecha: grilla de líneas -->
              <v-col cols="12" md="9">
                <DTELinesEditor
                  v-model="draft.lines"
                  :global-discount.sync="globalDiscount"
                  :tax-rate="taxRate"
                  @input="persistLines"
                  @valid-change="linesValid = $event"
                />
              </v-col>

            </v-row>
          </ValidationObserver>
        </div>

        <!-- PASO 2: PREVISUALIZACIÓN -->
        <div v-show="currentStep === 2" class="pa-6">
          <v-row justify="center">
            <v-col cols="12" md="10" lg="8">
              <div class="mb-4 d-flex align-center">
                <v-icon color="accent" class="mr-2">fas fa-eye</v-icon>
                <span class="subtitle-1 font-weight-black primary--text">Previsualización del Documento</span>
                <v-chip x-small color="warning" class="ml-3 font-weight-bold">PROFORMA</v-chip>
              </div>

              <!-- AC2/AC3: Banner de error con Reintentar — aparece tras un error de API -->
              <v-alert
                v-if="issuanceError"
                type="error"
                text
                border="left"
                class="mb-4"
              >
                <div class="d-flex align-center justify-space-between">
                  <div>
                    <div class="font-weight-bold">{{ issuanceError.message }}</div>
                    <div v-if="issuanceError.code" class="caption grey--text mt-1">
                      Código: {{ issuanceError.code }}
                    </div>
                  </div>
                  <v-btn
                    v-if="issuanceError.action === 'RETRY'"
                    small
                    color="accent"
                    class="ml-4 font-weight-bold flex-shrink-0"
                    :loading="submitting"
                    :disabled="submitting"
                    @click="submitDTE"
                  >
                    <v-icon left x-small>fas fa-redo</v-icon>
                    Reintentar
                  </v-btn>
                </div>
              </v-alert>

              <DTEVisualPreview
                :lines="draft.lines"
                :rut-recep="draft.rutRecep"
                :rzn-soc-recep="draft.rznSocRecep"
                :giro-recep="draft.giroRecep"
                :dir-recep="draft.dirRecep"
                :cmna-recep="draft.cmnaRecep"
                :ot-title="draft.otTitle"
                :tax-rate="taxRate"
                :global-discount="globalDiscount"
                :end-date="draft.endDate || null"
              />

              <v-alert type="info" text dense class="mt-4 caption">
                <strong>Revisa cuidadosamente.</strong> Al hacer clic en "Emitir DTE" se generará el folio legal en Relbase. Esta acción no se puede deshacer.
              </v-alert>
            </v-col>
          </v-row>
        </div>

      </div>
      <!-- ── FIN CONTENIDO ──────────────────────────────────────── -->

    </v-card>

    <!-- Confirm close -->
    <confirm
      v-model="showCloseConfirm"
      message="¿Cerrar sin emitir? El borrador se mantendrá guardado."
      @ok="forceClose"
      @cancel="showCloseConfirm = false"
    />

  </v-dialog>

  <!-- AC5: Snackbar turquesa con folio al emitir exitosamente -->
  <v-snackbar
    v-model="successSnackbar"
    color="#35BDC8"
    top
    right
    :timeout="6000"
    class="font-weight-bold"
  >
    <v-icon left color="white">fas fa-check-circle</v-icon>
    Documento emitido — Folio <strong class="ml-1">{{ successFolio }}</strong>
    <template #action="{ attrs }">
      <v-btn text color="white" v-bind="attrs" @click="successSnackbar = false">
        <v-icon small>fas fa-times</v-icon>
      </v-btn>
    </template>
  </v-snackbar>
  </div>
</template>

<script>
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import DTELinesEditor from './DTELinesEditor.vue'
import DTEVisualPreview from './DTEVisualPreview.vue'
import CustomerComplete2 from '@/components/customers/CustomerComplete2.vue'
import {
  fetchRelbaseSellers,
  fetchRelbasePaymentMethods,
  fetchRelbaseWarehouses,
  fetchRelbaseSaleChannels,
  fetchIssuedDocumentsByOT,
  issueRelbaseDTE,
  lookupProductInRelbase,
  lookupCustomerByRUT,
} from '@/api/relbase'
import { getWorkOrderforDTE } from '@/api/salesNotes'
import notifications from '@/utils/notifications'

export default {
  name: 'RelbaseIssuanceDialog',

  components: { ValidationObserver, ValidationProvider, DTELinesEditor, DTEVisualPreview, CustomerComplete2 },

  props: {
    value: {
      type: Boolean,
      default: false,
    },
    otId: {
      type: [Number, String],
      default: null,
    },
    // Tipo de documento: 33=Factura, 52=Guía de Despacho, 61=Nota de Crédito
    docType: {
      type: Number,
      default: 33,
    },
    // Story 4.3: Documento referenciado para modo Nota de Crédito
    referenceDoc: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      currentStep: 1,
      steps: ['Configuración', 'Previsualización'],
      showHistory: false,
      showCloseConfirm: false,
      loadingOT: false,
      loadingCatalogs: false,
      submitting: false,
      validatingLines: false,
      linesValid: false,
      rutChecking: false,
      rutValid: null,
      globalDiscount: 0,
      // AC5: Snackbar de éxito con folio
      successSnackbar: false,
      successFolio: '',
      // AC2/AC3 (Story 4.4): Banner de error inline con acción de reintento
      issuanceError: null,  // null o { message, code, action }
      // Story 4.3: Campos de referencia para NC
      ncCode: 1,
      ncReason: '',
      ncCodes: [
        { value: 1, text: '1 — Anula documento de referencia' },
        { value: 2, text: '2 — Corrige texto del documento' },
        { value: 3, text: '3 — Corrige montos del documento' },
      ],
      // Story 4.5: Date picker menu state
      endDateMenu: false,
      // Story 4.5: cliente seleccionado en modo generalizado (v-model local para CustomerComplete2)
      generalizedCustomer: null,
    }
  },

  computed: {
    internalVisible: {
      get() { return this.value },
      set(val) { this.$emit('input', val) },
    },
    draft() {
      return this.$store.getters['relbase/dteDraft']
    },
    issuedDocuments() {
      return this.$store.getters['relbase/issuedDocuments']
    },
    relbaseSellers() {
      return this.$store.getters['relbase/relbaseSellers']
    },
    relbasePaymentMethods() {
      return this.$store.getters['relbase/relbasePaymentMethods']
    },
    // AC4: Catálogos de Bodega y Canal de Venta
    relbaseWarehouses() {
      return this.$store.getters['relbase/relbaseWarehouses']
    },
    relbaseSalesChannels() {
      return this.$store.getters['relbase/relbaseSalesChannels']
    },
    taxRate() {
      return parseInt(this.$store.getters.getCompany?.tax || 19)
    },
    // Story 4.5: Modo Generalizado — cuando no hay OT inicial
    isGeneralizedMode() {
      return !this.otId && !this.referenceDoc
    },
    // Story 4.5: end_date del borrador con getter/setter
    endDate: {
      get() { return this.draft.endDate || null },
      set(val) { this.$store.commit('relbase/SET_DRAFT_END_DATE', val) },
    },
    // Formateo de end_date para display
    endDateFormatted() {
      if (!this.endDate) return ''
      const [y, m, d] = this.endDate.split('-')
      return `${d}/${m}/${y}`
    },
    // AC2: Detectar problemas de sincronización que bloquean la emisión
    syncIssues() {
      const issues = []
      if (!this.draft.relbaseCustomerId) {
        issues.push('El cliente no está sincronizado con Relbase (falta relbase_customer_id).')
      }
      if (!this.draft.formaPago) issues.push('Selecciona una Forma de Pago.')
      if (!this.draft.bodega) issues.push('Selecciona una Bodega.')
      if (!this.draft.canalVenta) issues.push('Selecciona un Canal de Venta.')
      return issues
    },
    canAdvance() {
      return this.draft.lines.length > 0
    },
  },

  watch: {
    value(newVal) {
      if (newVal) {
        this.onDialogOpen()
      }
    },
    globalDiscount(val) {
      this.$store.commit('relbase/SET_DRAFT_GLOBAL_DISCOUNT', val)
    },
  },

  methods: {
    async onDialogOpen() {
      this.currentStep = 1
      this.showHistory = false
      this.globalDiscount = this.draft.globalDiscount || 0
      this.rutValid = null
      this.rutChecking = false
      this.issuanceError = null
      this.endDateMenu = false
      this.generalizedCustomer = null
      this.ncCode = 1
      this.ncReason = ''
      await this.loadOTContext()
      await this.loadCatalogs()
      // Story 4.5: Establecer end_date default si no hay uno guardado
      if (!this.draft.endDate) {
        this.$store.commit('relbase/SET_DRAFT_END_DATE', this.computeDefaultEndDate())
      }
    },

    // Story 4.5: Calcula end_date default según tipo de documento y días de crédito del cliente
    computeDefaultEndDate(paymentDays) {
      const days = paymentDays !== undefined ? paymentDays : (this.draft.payment || 0)
      const base = new Date()
      if (this.docType === 33 && days > 0) {
        base.setDate(base.getDate() + parseInt(days))
      }
      return base.toISOString().slice(0, 10)
    },

    // Story 4.5: Cliente seleccionado en Modo Generalizado via CustomerComplete2
    onGeneralizedCustomerSelected(customer) {
      if (!customer) return
      const address = customer.addresses?.length
        ? `${customer.addresses[0].street || ''} ${customer.addresses[0].number || ''}`.trim()
        : ''
      const comune = customer.addresses?.length ? (customer.addresses[0].comune || '') : ''
      const city = customer.addresses?.length ? (customer.addresses[0].city || '') : ''
      const contact = customer.contacts?.length
        ? `${customer.contacts[0].first_name || ''} ${customer.contacts[0].last_name || ''}`.trim()
        : ''
      this.$store.commit('relbase/SET_DRAFT_HEADERS', {
        customerId: customer.id,
        relbaseCustomerId: customer.relbase_customer_id || null,
        rutRecep: customer.rut,
        rznSocRecep: customer.name,
        giroRecep: customer.giro || '',
        dirRecep: address,
        cmnaRecep: comune,
        strCity: city,
        contactRecep: contact,
        payment: customer.payment || 0,
      })
      // Recalcular end_date con los días de crédito del cliente
      this.$store.commit('relbase/SET_DRAFT_END_DATE', this.computeDefaultEndDate(customer.payment || 0))
      this.rutValid = customer.relbase_customer_id ? true : false
    },

    async onRutBlur() {
      const rut = (this.draft.rutRecep || '').trim()
      if (!rut) return
      this.rutChecking = true
      this.rutValid = null
      const result = await lookupCustomerByRUT(rut)
      this.rutChecking = false
      this.rutValid = result.found
      if (result.found) {
        // Actualizar campos del receptor con datos de Relbase
        this.$store.commit('relbase/SET_DRAFT_HEADERS', {
          ...this.draft,
          relbaseCustomerId: String(result.relbase_id),
          rznSocRecep: result.name || this.draft.rznSocRecep,
          giroRecep: result.business_activity || this.draft.giroRecep,
          dirRecep: result.address || this.draft.dirRecep,
          cmnaRecep: result.commune || this.draft.cmnaRecep,
          strCity: result.city || this.draft.strCity,
        })
      } else {
        this.$store.commit('relbase/SET_DRAFT_HEADERS', {
          ...this.draft,
          relbaseCustomerId: null,
        })
      }
    },

    // AC4: Cargar los 4 catálogos necesarios
    async loadCatalogs() {
      const needSellers = this.relbaseSellers.length === 0
      const needPayMethods = this.relbasePaymentMethods.length === 0
      const needWarehouses = this.relbaseWarehouses.length === 0
      const needChannels = this.relbaseSalesChannels.length === 0

      if (!needSellers && !needPayMethods && !needWarehouses && !needChannels) return

      this.loadingCatalogs = true
      try {
        const promises = []
        if (needSellers) promises.push(fetchRelbaseSellers())
        else promises.push(Promise.resolve(null))

        if (needPayMethods) promises.push(fetchRelbasePaymentMethods())
        else promises.push(Promise.resolve(null))

        if (needWarehouses) promises.push(fetchRelbaseWarehouses())
        else promises.push(Promise.resolve(null))

        if (needChannels) promises.push(fetchRelbaseSaleChannels())
        else promises.push(Promise.resolve(null))

        const [sellers, payMethods, warehouses, channels] = await Promise.all(promises)

        if (sellers) this.$store.commit('relbase/SET_RELBASE_SELLERS', sellers)
        if (payMethods) this.$store.commit('relbase/SET_RELBASE_PAYMENT_METHODS', payMethods)
        if (warehouses) this.$store.commit('relbase/SET_RELBASE_WAREHOUSES', warehouses)
        if (channels) this.$store.commit('relbase/SET_RELBASE_SALES_CHANNELS', channels)
      } catch {
        // Catálogos no críticos, continuar sin ellos
      } finally {
        this.loadingCatalogs = false
      }
    },

    async loadOTContext() {
      if (!this.otId) return

      // Si ya hay un borrador para esta misma OT con líneas, no recargar líneas
      // pero sí intentar auto-poblar el vendedor si falta
      if (this.draft.otId === this.otId && this.draft.lines.length > 0) {
        this.$store.commit('relbase/ENSURE_DRAFT_REQUEST_ID')
        // Si el borrador no tiene vendedor, intentar obtenerlo del OT
        if (!this.draft.vendedor) {
          try {
            const data = await getWorkOrderforDTE(this.otId)
            const sellerRelbaseId = data.customer?.seller?.relbase_seller_id || null
            if (sellerRelbaseId) {
              this.$store.commit('relbase/SET_DRAFT_SELECTORS', { vendedor: sellerRelbaseId })
            }
          } catch { /* ignorar */ }
        }
        return
      }

      this.loadingOT = true
      try {
        const data = await getWorkOrderforDTE(this.otId)
        const {
          customer, title, products, id, additional_costs, totals,
        } = data

        // AC2: Capturar relbase_customer_id del cliente
        this.$store.commit('relbase/SET_DRAFT_HEADERS', {
          otId: id,
          otTitle: title,
          customerId: customer.id,
          relbaseCustomerId: customer.relbase_customer_id || null,
          rutRecep: customer.rut,
          rznSocRecep: customer.name,
          giroRecep: customer.giro,
          payment: customer.payment,
          dirRecep: customer.addresses?.length
            ? `${customer.addresses[0].street} ${customer.addresses[0].number}`
            : '',
          cmnaRecep: customer.addresses?.length ? customer.addresses[0].comune : '',
          strCity: customer.addresses?.length ? (customer.addresses[0].city || '') : '',
          contactRecep: customer.contacts?.length
            ? `${customer.contacts[0].first_name} ${customer.contacts[0].last_name}`
            : '',
          glosa: title,
        })

        // Pre-seleccionar vendedor desde el seller del cliente (si tiene relbase_seller_id)
        const sellerRelbaseId = customer.seller?.relbase_seller_id || null
        if (sellerRelbaseId) {
          this.$store.commit('relbase/SET_DRAFT_SELECTORS', { vendedor: sellerRelbaseId })
        }

        // AC2: Líneas con relbase_product_id incluido
        const lines = []
        products.forEach((p, i) => {
          lines.push({
            nro_linea: i + 1,
            product: p.id,
            sku: p.sku,
            name: p.name,
            unit: p.sell_unit_m2 ? 'MTS2' : 'UN',
            qty: p.sell_unit_m2 ? p.totalMts2 : p.qty,
            price: Math.round(p.price * (1 - (totals.discount || 0) * 0.01)),
            discount: 0,
            afecto: true,
            relbase_product_id: p.relbase_product_id || null,  // AC2
          })
        })
        if (additional_costs) {
          additional_costs.forEach((c, i) => {
            lines.push({
              nro_linea: products.length + i + 1,
              product: -1,  // -1 = costo adicional, no se valida sync
              sku: c.name.toUpperCase().slice(0, 4),
              name: c.name,
              unit: c.cost_unit || 'UN',
              qty: c.unit === 1 ? totals.mts2 : c.qty,
              price: Math.round(c.sell_price * (1 - (totals.discount || 0) * 0.01)),
              discount: 0,
              afecto: true,
              relbase_product_id: null,  // costos adicionales no necesitan sync
            })
          })
        }
        this.$store.commit('relbase/SET_DRAFT_LINES', lines)

        // AC1: Generar requestId único para esta emisión
        this.$store.commit('relbase/ENSURE_DRAFT_REQUEST_ID')

        // Story 4.5: Calcular end_date default con los días de crédito del cliente de la OT
        if (!this.draft.endDate) {
          this.$store.commit('relbase/SET_DRAFT_END_DATE', this.computeDefaultEndDate(customer.payment || 0))
        }

        // Cargar historial de documentos emitidos para esta OT (relación 1:N)
        try {
          const docs = await fetchIssuedDocumentsByOT(id)
          const docList = Array.isArray(docs) ? docs : (docs.results || [])
          this.$store.commit('relbase/SET_ISSUED_DOCUMENTS', docList)
        } catch {
          // Historial no crítico
        }
      } catch {
        notifications.showError('Error al cargar datos de la OT')
      } finally {
        this.loadingOT = false
      }
    },

    persistHeaders() {
      this.$store.commit('relbase/SET_DRAFT_HEADERS', {
        rutRecep: this.draft.rutRecep,
        rznSocRecep: this.draft.rznSocRecep,
        giroRecep: this.draft.giroRecep,
        dirRecep: this.draft.dirRecep,
        cmnaRecep: this.draft.cmnaRecep,
        glosa: this.draft.glosa,
      })
    },

    persistLines(lines) {
      this.$store.commit('relbase/SET_DRAFT_LINES', lines)
    },

    persistSelectors() {
      this.$store.commit('relbase/SET_DRAFT_SELECTORS', {
        bodega: this.draft.bodega,
        vendedor: this.draft.vendedor,
        canalVenta: this.draft.canalVenta,
        formaPago: this.draft.formaPago,
        terminosPago: this.draft.terminosPago,
      })
    },

    async goToPreview() {
      const valid = await this.$refs.stepObserver.validate()
      if (!valid) {
        notifications.showError('Completa los datos requeridos del receptor')
        return
      }
      if (this.draft.lines.length === 0) {
        notifications.showError('Agrega al menos una línea de detalle')
        return
      }
      // DTELinesEditor ya validó y persistió relbase_product_id en cada línea
      this.currentStep = 2
    },

    // AC1 + AC4 + AC5 + AC6: Emisión completa con idempotencia, payload enriquecido y transitividad
    async submitDTE() {
      if (this.syncIssues.length > 0) return  // Doble guarda AC2

      this.submitting = true
      try {
        // AC1: Garantizar que el request_id esté generado antes de emitir
        this.$store.commit('relbase/ENSURE_DRAFT_REQUEST_ID')

        // Construir el payload para Relbase con los nombres de campo exactos de la API
        const today = new Date().toISOString().slice(0, 10)
        const relbasePayload = {
          type_document: 1001,                              // TODO: mapear por tipo en producción
          type_document_sii: this.docType,                 // 33=Factura, 52=Guía, 61=NC
          start_date: today,
          end_date: this.draft.endDate || today,            // Story 4.5: fecha de vencimiento dinámica
          customer_id: parseInt(this.draft.relbaseCustomerId),
          address: this.draft.dirRecep || '',
          is_str_city_and_comune: true,
          str_commune: this.draft.cmnaRecep || '',
          str_city: this.draft.strCity || '',
          seller_id: parseInt(this.draft.vendedor) || null,
          ware_house_id: parseInt(this.draft.bodega) || null,
          channel_id: parseInt(this.draft.canalVenta) || null,
          type_payment_id: parseInt(this.draft.formaPago) || null,
          comment: [this.draft.otId ? `OT-${this.draft.otId}` : '', this.draft.glosa || ''].filter(Boolean).join(' - '),
          label_value: 'string',
          type_transfer: 0,
          contact: this.draft.contactRecep || '',
          paid: false,
          mnt_bruto: true,
          amount_paid: 0,
          change_due: 0,
          products: this.draft.lines.map(line => ({
            product_id: parseInt(line.relbase_product_id),
            code: line.sku || '',
            price: line.price,
            quantity: line.qty,
            tax_affected: line.afecto !== false,
            description: line.name,
            unit_item: line.unit || 'Unidad',
          })),
        }

        // Story 4.3 (AC4): Agregar referencias si es NC con documento referenciado
        if (this.referenceDoc) {
          relbasePayload.references = [
            {
              folio: this.referenceDoc.folio,
              type_document_sii: this.referenceDoc.doc_type,
              date: this.referenceDoc.date_emited,
              reason: this.ncReason || `Nota de Crédito referencia folio ${this.referenceDoc.folio}`,
              code: this.ncCode,
            },
          ]
        }

        // Payload completo para el backend Django
        const payload = {
          request_id: this.draft.requestId,    // AC1: idempotencia
          doc_type: this.docType,
          work_order_id: this.draft.otId,
          customer_id: this.draft.customerId,
          relbase_seller_id: String(this.draft.vendedor || ''),
          relbase_payload: relbasePayload,
        }

        const result = await issueRelbaseDTE(payload)

        // AC5: Snackbar turquesa con folio generado
        this.successFolio = result.folio || ''
        this.successSnackbar = true
        this.issuanceError = null  // limpiar cualquier error previo al emitir con éxito

        // AC4 (Story 4.4): el draft se resetea SOLO en éxito
        const issuedOtId = this.draft.otId || this.otId
        this.$store.dispatch('relbase/resetDraft')

        // Recargar historial con el nuevo documento incluido
        if (issuedOtId) {
          try {
            const docs = await fetchIssuedDocumentsByOT(issuedOtId)
            this.$store.commit('relbase/SET_ISSUED_DOCUMENTS',
              Array.isArray(docs) ? docs : (docs.results || []))
          } catch { /* no crítico */ }
        }

        // AC5: Emitir evento para que el padre pueda actualizar la OT
        this.$emit('issued', { folio: this.successFolio, docType: this.docType })
        this.internalVisible = false
      } catch (e) {
        // AC1/AC4 (Story 4.4): mostrar banner con error estructurado — el draft NO se resetea
        const data = e?.response?.data || {}
        this.issuanceError = {
          message: data.message || e?.message || 'Ocurrió un error inesperado al emitir el DTE.',
          code: data.code || '',
          action: data.action || 'RETRY',
        }
      } finally {
        this.submitting = false
      }
    },

    goBack() {
      this.currentStep = 1
      this.issuanceError = null
    },

    requestClose() {
      if (this.draft.lines.length > 0 || this.draft.rutRecep) {
        this.showCloseConfirm = true
      } else {
        this.forceClose()
      }
    },

    forceClose() {
      this.showCloseConfirm = false
      this.internalVisible = false
    },
  },
}
</script>

<style scoped>
.issuance-dialog-card {
  background-color: #f4f7f9;
}

.issuance-app-bar {
  height: 64px;
  min-height: 64px;
  max-height: 64px;
  background: #1A1F2E !important;
}

.stepper-indicator {
  background: white;
  border-bottom: 1px solid rgba(0,0,0,0.06);
  min-height: 52px;
}

.step-circle {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: #e0e7ef;
  color: #90a4ae;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.25s ease;
}
.step-active {
  background: #35BDC8 !important;
  color: white !important;
}
.step-done {
  background: #4CAF50 !important;
  color: white !important;
}

.history-panel {
  background: #f8fafc;
}

.issuance-body {
  background-color: #f4f7f9;
}

/* Cards */
.modern-card {
  border-radius: 8px !important;
  border: 1px solid rgba(0,0,0,0.06) !important;
  background: white !important;
}

.card-section-title {
  font-size: 0.72rem;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  color: #1A1F2E;
  display: flex;
  align-items: center;
}

.field-label {
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #90a4ae;
  margin-bottom: 4px;
  margin-left: 2px;
}

/* Inputs */
.modern-input >>> .v-input__control fieldset {
  border-radius: 8px !important;
  border-color: #e0e4e8 !important;
}
.modern-input >>> input {
  font-size: 0.875rem !important;
  font-weight: 500 !important;
  color: #1A1F2E !important;
}

.lh-1 { line-height: 1 !important; }

.rut-valid >>> .v-input__control fieldset {
  border-color: #4caf50 !important;
}
.rut-invalid >>> .v-input__control fieldset {
  border-color: #f44336 !important;
}
</style>
